import { useState, useEffect, useRef, useMemo } from 'react';
import { CH, I, IconBtn } from './MobileAppHeader';
import { api } from '../../apiClient';

const STATUS_OPTIONS = [
  { value: '', label: '未設定' },
  { value: 'enrolled', label: 'Enrolled' },
  { value: 'learning', label: 'Learning' },
  { value: 'certified', label: 'Certified' },
  { value: 'retired', label: 'Retired' },
];

const statusMeta = {
  learning:  { label: 'Learning',  bg: '#e7f5ff', fg: '#1c7ed6' },
  certified: { label: 'Certified', bg: '#ebfbee', fg: '#2f9e44' },
  enrolled:  { label: 'Enrolled',  bg: '#f3f0ff', fg: '#7048e8' },
  retired:   { label: 'Retired',   bg: '#f1f3f5', fg: '#868e96' },
};

const Pill = ({ active, onClick, children }) => (
  <div onClick={onClick} style={{
    height: 30, padding: '0 14px', borderRadius: 16,
    background: active ? '#c96442' : 'var(--app-color-surface)',
    color: active ? '#fff' : CH.ink,
    border: `1px solid ${active ? '#c96442' : 'var(--app-color-border)'}`,
    fontSize: 12, fontFamily: CH.serif, fontWeight: 500,
    display: 'inline-flex', alignItems: 'center',
    whiteSpace: 'nowrap', cursor: 'pointer', flexShrink: 0,
  }}>{children}</div>
);

function CertRow({ cert, onSave, saving, saved }) {
  const sm = statusMeta[cert.status];
  const vendorColor = cert.vendor === 'aws' ? '#e8590c' : cert.vendor === 'google' ? '#34A853' : '#1c7ed6';
  const vendorLabel = cert.vendor === 'aws' ? 'AWS' : cert.vendor === 'microsoft' ? 'Microsoft' : cert.vendor === 'google' ? 'Google' : cert.vendor;

  return (
    <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--app-color-border)', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', background: `${vendorColor}22`, color: vendorColor, fontFamily: CH.serif, fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 4 }}>{vendorLabel}</span>
          </div>
          <div style={{ fontSize: 14, fontWeight: 500, color: CH.ink, letterSpacing: -0.2 }}>{cert.formal_name || cert.name}</div>
          <div style={{ fontFamily: CH.mono, fontSize: 10, color: CH.dim, marginTop: 2 }}>{cert.name}</div>
        </div>
        {sm ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', background: sm.bg, color: sm.fg, fontFamily: CH.serif, fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 4, flexShrink: 0 }}>{sm.label}</span>
        ) : (
          <span style={{ fontSize: 11, color: CH.dim, fontFamily: CH.serif, fontStyle: 'italic', flexShrink: 0 }}>未設定</span>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, paddingTop: 6, borderTop: '1px dashed var(--app-color-border)', alignItems: 'center' }}>
        <div>
          <div style={{ fontFamily: CH.mono, fontSize: 9, color: CH.dim, textTransform: 'uppercase', marginBottom: 4 }}>ステータス</div>
          <select
            value={cert.status ?? ''}
            onChange={e => onSave(cert.id, 'status', e.target.value)}
            disabled={saving}
            style={{
              width: '100%', height: 30, padding: '0 8px',
              background: 'var(--mantine-color-body)', border: '1px solid var(--app-color-border)',
              borderRadius: 6, fontSize: 12, fontFamily: CH.serif, color: CH.ink,
            }}
          >
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontFamily: CH.mono, fontSize: 9, color: CH.dim, textTransform: 'uppercase', marginBottom: 4 }}>取得日</div>
          <input
            type="date"
            value={cert.acquired_at ? cert.acquired_at.slice(0, 10) : ''}
            onChange={e => onSave(cert.id, 'acquired_at', e.target.value || null)}
            disabled={saving}
            style={{
              width: '100%', height: 30, padding: '0 6px',
              background: 'var(--mantine-color-body)', border: '1px solid var(--app-color-border)',
              borderRadius: 6, fontSize: 11, fontFamily: CH.mono, color: cert.acquired_at ? CH.ink : CH.dim,
              boxSizing: 'border-box',
            }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <div
            onClick={() => onSave(cert.id, 'show_on_home', !(cert.show_on_home !== false))}
            style={{
              width: 20, height: 20, borderRadius: 4,
              border: `1.5px solid ${cert.show_on_home !== false ? '#c96442' : CH.dim}`,
              background: cert.show_on_home !== false ? '#c96442' : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            {cert.show_on_home !== false && <span style={{ color: '#fff', fontSize: 10, lineHeight: 1 }}>✓</span>}
          </div>
          <div style={{ fontSize: 9, color: CH.dim, fontFamily: CH.mono }}>HOME</div>
          {saved && <span style={{ fontSize: 9, color: '#099268' }}>✓</span>}
          {saving && <span style={{ fontSize: 9, color: CH.dim }}>…</span>}
        </div>
      </div>
    </div>
  );
}

function MobileCertListScreen({ onBack }) {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState({});
  const [saved, setSaved] = useState({});
  const [selectedVendor, setSelectedVendor] = useState('all');
  const timerRefs = useRef({});
  const certsRef = useRef(certs);
  useEffect(() => { certsRef.current = certs; }, [certs]);

  useEffect(() => {
    api.get('/user-certifications/all-statuses')
      .then(data => setCerts(data ?? []))
      .catch(() => setError('データの取得に失敗しました。'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = (certId, field, value) => {
    setCerts(prev => prev.map(c => c.id === certId ? { ...c, [field]: value } : c));
    if (timerRefs.current[certId]) clearTimeout(timerRefs.current[certId]);
    timerRefs.current[certId] = setTimeout(async () => {
      const cert = certsRef.current.find(c => c.id === certId);
      if (!cert) return;
      const updated = { ...cert, [field]: value };
      setSaving(prev => ({ ...prev, [certId]: true }));
      try {
        await api.put('/user-certification-details', {
          certificationId: certId,
          status: updated.status || null,
          acquired_at: updated.acquired_at || null,
          expires_at: updated.expires_at || null,
          show_on_home: updated.show_on_home !== false,
        });
        setSaved(prev => ({ ...prev, [certId]: true }));
        setTimeout(() => setSaved(prev => ({ ...prev, [certId]: false })), 2000);
      } catch { setError('保存に失敗しました。'); }
      finally { setSaving(prev => ({ ...prev, [certId]: false })); }
    }, 800);
  };

  const byVendor = useMemo(() => certs.reduce((acc, c) => {
    const v = (c.vendor ?? 'other').toLowerCase();
    if (!acc[v]) acc[v] = [];
    acc[v].push(c);
    return acc;
  }, {}), [certs]);

  const vendorKeys = Object.keys(byVendor);
  const vendorLabel = { aws: 'AWS', microsoft: 'Microsoft', google: 'Google' };

  const visibleCerts = selectedVendor === 'all'
    ? certs
    : (byVendor[selectedVendor] ?? []);

  return (
    <div style={{ background: 'var(--mantine-color-body)', minHeight: '100vh', fontFamily: CH.serif, paddingBottom: 40 }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 20,
        background: 'var(--mantine-color-body)',
        borderBottom: '1px solid var(--app-color-border)',
        padding: '8px 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 48,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <IconBtn onClick={onBack}>{I.back}</IconBtn>
          <span style={{ fontSize: 14, fontWeight: 500, color: CH.ink }}>Certifications</span>
        </div>
      </div>

      {/* ベンダーフィルタ */}
      <div style={{ padding: '14px 16px 12px' }}>
        <div style={{ fontSize: 11, color: CH.dim, fontFamily: CH.mono, marginBottom: 8, textTransform: 'uppercase' }}>ベンダー</div>
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Pill active={selectedVendor === 'all'} onClick={() => setSelectedVendor('all')}>すべて ({certs.length})</Pill>
          {vendorKeys.map(v => (
            <Pill key={v} active={selectedVendor === v} onClick={() => setSelectedVendor(v)}>
              {vendorLabel[v] ?? v} ({byVendor[v].length})
            </Pill>
          ))}
        </div>
      </div>

      {error && (
        <div style={{ margin: '0 16px 12px', padding: '10px 14px', background: '#fff0ee', border: '1px solid #c03727', borderRadius: 8, fontSize: 13, color: '#c03727' }}>{error}</div>
      )}

      {loading ? (
        <div style={{ padding: '40px 20px', textAlign: 'center', color: CH.dim, fontSize: 13 }}>読み込み中...</div>
      ) : (
        <div style={{ background: 'var(--app-color-surface)', margin: '0 16px', borderRadius: 10, border: '1px solid var(--app-color-border)', overflow: 'hidden' }}>
          {visibleCerts.map(c => (
            <CertRow key={c.id} cert={c} onSave={handleSave} saving={saving[c.id]} saved={saved[c.id]} />
          ))}
        </div>
      )}

      <div style={{ padding: '14px 20px 24px', fontSize: 11, color: CH.dim, fontFamily: CH.mono, textAlign: 'center' }}>
        変更は 0.8 秒後に自動保存されます
      </div>
    </div>
  );
}

export default MobileCertListScreen;
